import { TestIDs } from 'src/constants/testIDs.constants';

// core
export const generateTestID = (...args: Array<string | TestIDs | undefined>): string =>
  args.filter(a => typeof a !== 'undefined').join('.');

export const generateSelectedTestID = (id: TestIDs | string, isSelected?: boolean): string =>
  generateTestID(id, isSelected ? TestIDs.SELECTED : undefined);

export const mobileTestID = (id: TestIDs | string): string => `${id}-mobile`;

export const desktopTestID = (id: TestIDs | string): string => `${id}-desktop`;

export const wrapperTestID = (id: TestIDs | string): string => `wrapper-${id}`;

// drawer

export const drawerTestID = (drawerID: TestIDs | string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(`drawer-${drawerID}`, ...ids);

// modal
export const modalTestID = (modalID: TestIDs | string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(`modal-${modalID}`, ...ids);

export const wrapperModalTestID = (modalID: TestIDs, ...ids: Array<TestIDs | string>): string =>
  wrapperTestID(modalTestID(modalID, ...ids));

// bottom sheet
export const bottomSheetTestID = (bottomSheetID: TestIDs | string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(`bottom-sheet-${bottomSheetID}`, ...ids);

export const wrapperBottomSheetTestID = (bottomSheetID: TestIDs, ...ids: Array<TestIDs | string>): string =>
  wrapperTestID(bottomSheetTestID(bottomSheetID, ...ids));

// bottom-bar
export const bottomBarTestID = (...ids: Array<TestIDs | string>): string => generateTestID(TestIDs.BOTTOM_BAR, ...ids);

export const bottomBarEditTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.BOTTOM_BAR_EDIT, ...ids);

// quality-alert
export const qualityAlertBadgeTestID = (
  productId?: string,
  userImageAreaId?: string,
  ...ids: Array<TestIDs | string>
): string => generateTestID(`quality-alert-badge${productId ? '-' + productId + '-' + userImageAreaId : ''}`, ...ids);

// crop-alert
export const cropAlertBadgeTestID = (
  productId: string,
  userImageAreaId: string,
  ...ids: Array<TestIDs | string>
): string => generateTestID(`crop-alert-badge-${productId}-${userImageAreaId}`, ...ids);

// scroll-view
export const scrollViewElementTestID = (id: string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(`scroll-view-element-${id}`, ...ids);

// choose-photos
export const choosePhotosBannerTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.CHOOSE_PHOTOS_BANNER, ...ids);

// product-list
export const productListDesktopTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(desktopTestID(TestIDs.PRODUCT_LIST), ...ids);

export const productListMobileTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(mobileTestID(TestIDs.PRODUCT_LIST), ...ids);

// picture-viewer
export const pictureViewerHeaderTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.PICTURE_VIER_HEADER, ...ids);

// header
export const headerEditorTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.HEADER_EDITOR, ...ids);

// footer
export const footerEditorTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.FOOTER_EDITOR, ...ids);

// slider
export const sliderZoomTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.SLIDER_ZOOM, ...ids);

// tooltip
export const tooltipTestID = (tooltipID: TestIDs | string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(tooltipID, ...ids);

// group-filter
export const filterGroupTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.FILTER_GROUP, ...ids);

// toast notification
export const toastNotificationTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.TOAST_NOTIFICATION, ...ids);

// lookups bar
export const lookupsBarTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.LOOKUPS_BAR, ...ids);

export const configurationTestID = (code: string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(`configuration-${code}`, ...ids);

export const lookupChoiceTestID = (
  optionCode: string,
  choiceIndex: number | string,
  ...ids: Array<TestIDs | string>
): string => generateTestID(`lookup-choice-${optionCode}-${String(choiceIndex)}`, ...ids);

// sliders
export const sliderRotationTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.SLIDER_ROTATION, ...ids);

// filter
export const filterTestID = (...ids: Array<TestIDs | string>): string => generateTestID(TestIDs.FILTER, ...ids);

// product
export const productTestID = (productID: string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(`product-${productID}`, ...ids);

export const productPreviewTestID = (
  productID: string,
  productType: string | undefined,
  ...ids: Array<TestIDs | string>
): string => generateTestID(`product-preview${productType ? '-' + productType : ''}-${productID}`, ...ids);

export const userImageAreaTestID = (userImageAreaID: string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(`user-image-area-${userImageAreaID}`, ...ids);

export const productContentHeaderTestID = (productId: string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(productTestID(productId), TestIDs.CONTENT_HEADER, ...ids);

export const productContentFooterTestID = (productId: string, ...ids: Array<TestIDs | string>): string =>
  generateTestID(productTestID(productId), TestIDs.CONTENT_FOOTER, ...ids);

// adjustments
export const adjustmentsTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.ADJUSTMENTS, ...ids);

// adjust-crops grid item — keyed by list index rather than the underlying data id,
// since userImageArea ids are not guaranteed unique across grid items.
export const adjustCropsItemTestID = (index: number, ...ids: Array<TestIDs | string>): string =>
  generateTestID(`${TestIDs.ADJUST_CROPS}-item-${index}`, ...ids);

// group tab
export const groupTabTestID = (groupCode: string): string => generateTestID(TestIDs.GROUP_TAB, groupCode);

export const groupActionTabTestID = (groupCode: string, actionId: TestIDs | string): string =>
  generateTestID(groupTabTestID(groupCode), actionId);

// discount scale
export const discountScaleInfoDropupTestID = (...ids: Array<TestIDs | string>): string =>
  generateTestID(TestIDs.DISCOUNT_SCALE_INFO_DROPUP, ...ids);
